import { apiService } from './apiService';
import { UserDemographicsData } from '../components/UserDemographics';

export interface ReferenceRangeResponse {
  testName: string;
  referenceMin: number | null;
  referenceMax: number | null;
  units: string;
  source: string;
  notes?: string;
  cached?: boolean;
}

export class ReferenceRangeService {
  // Rate limiting
  private static lastRequestTime = 0;
  private static requestCount = 0;
  private static requestWindowStart = Date.now();
  private static readonly MIN_REQUEST_INTERVAL = 1000; // Minimum 1 second between requests
  private static readonly MAX_REQUESTS_PER_MINUTE = 20;
  
  static async fetchReferenceRange(
    testName: string, 
    units: string,
    sessionId: string,
    demographics?: UserDemographicsData
  ): Promise<ReferenceRangeResponse | null> {
    // Rate limiting check
    const now = Date.now();
    
    // Reset request count every minute
    if (now - this.requestWindowStart > 60000) {
      this.requestCount = 0;
      this.requestWindowStart = now;
    }
    
    // Check if we've exceeded rate limit
    if (this.requestCount >= this.MAX_REQUESTS_PER_MINUTE) {
      console.warn('Rate limit exceeded for reference range API');
      throw new Error('Rate limit exceeded. Please try again later.');
    }
    
    // Enforce minimum interval between requests
    const timeSinceLastRequest = now - this.lastRequestTime;
    if (timeSinceLastRequest < this.MIN_REQUEST_INTERVAL) {
      const waitTime = this.MIN_REQUEST_INTERVAL - timeSinceLastRequest;
      console.log(`Rate limiting: waiting ${waitTime}ms before next request`);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
    
    this.lastRequestTime = Date.now();
    this.requestCount++;
    
    try {
      console.log('📡 API Request:', {
        url: '/api/reference-range/fetch',
        testName,
        units,
        sessionId,
        requestCount: this.requestCount,
        requestsRemaining: this.MAX_REQUESTS_PER_MINUTE - this.requestCount,
        timeSinceWindowStart: Math.round((Date.now() - this.requestWindowStart) / 1000) + 's'
      });
      
      const response = await apiService.post('/api/reference-range/fetch', {
        testName,
        units,
        sessionId,
        age: demographics?.age,
        sex: demographics?.sex,
        isPregnant: demographics?.isPregnant
      });

      console.log('Reference range API response:', response);

      if (response.status === 'success') {
        return response.data;
      }
      
      return null;
    } catch (error: any) {
      console.error('Error fetching reference range:', error);
      if (error.status) {
        console.error('Response status:', error.status);
        console.error('Error message:', error.message);
        
        // If we get a 429 (Too Many Requests), throw to trigger exponential backoff
        if (error.status === 429) {
          throw new Error('API rate limit exceeded');
        }
      }
      return null;
    }
  }

  // Cache fetched reference ranges to avoid repeated API calls
  private static cache = new Map<string, ReferenceRangeResponse>();

  static async getCachedOrFetch(
    testName: string,
    units: string,
    sessionId: string,
    demographics?: UserDemographicsData
  ): Promise<ReferenceRangeResponse | null> {
    // Include demographics in cache key
    const demographicsKey = demographics ? `${demographics.age || 'NA'}-${demographics.sex || 'NA'}-${demographics.isPregnant ? 'P' : ''}` : 'no-demo';
    const cacheKey = `${testName.toLowerCase()}-${units.toLowerCase()}-${demographicsKey}`;
    
    console.log('getCachedOrFetch called:', { testName, units, demographics, cacheKey });
    
    if (this.cache.has(cacheKey)) {
      console.log('Returning cached reference range for:', cacheKey);
      return this.cache.get(cacheKey)!;
    }

    console.log('No cache hit, fetching from API for:', cacheKey);
    const result = await this.fetchReferenceRange(testName, units, sessionId, demographics);
    if (result) {
      console.log('Caching reference range for:', cacheKey);
      this.cache.set(cacheKey, result);
    }

    return result;
  }

  static clearCache() {
    this.cache.clear();
  }

  static clearCacheForTest(testName: string, units: string) {
    // Clear all cache entries for this test (regardless of demographics)
    const prefix = `${testName.toLowerCase()}-${units.toLowerCase()}`;
    Array.from(this.cache.keys())
      .filter(key => key.startsWith(prefix))
      .forEach(key => this.cache.delete(key));
  }
}